import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import KenModal, { kenModalPrimaryButtonSx } from '../KenModal';
import KenButton from '../KenButton';
import { getMimeTypeForFileName } from '../../Utils/fileMimeTypes';

interface FilePreviewState {
    open: boolean;
    url: string | null;
    fileName: string;
    mimeType: string;
}

const CLOSED_STATE: FilePreviewState = { open: false, url: null, fileName: '', mimeType: '' };

// Renders whatever's fetched (PDF/image/video) inline in a large in-app
// modal instead of a new browser tab — shared by Course Artifacts and
// Activity Submissions viewing, faculty and student portals alike.
export const useFilePreview = () => {
    const [state, setState] = useState<FilePreviewState>(CLOSED_STATE);
    const activeUrlRef = useRef<string | null>(null);

    const close = useCallback(() => {
        if (activeUrlRef.current) {
            URL.revokeObjectURL(activeUrlRef.current);
            activeUrlRef.current = null;
        }
        setState(CLOSED_STATE);
    }, []);

    // Accepts either a raw ArrayBuffer/Uint8Array (builds its own object
    // URL, revoked on close) plus a fileName to derive the MIME type from,
    // or an already-resolved {url, mimeType} pair if the caller built its
    // own blob URL already.
    const show = useCallback((input: { buffer: ArrayBuffer; fileName: string } | { url: string; fileName: string; mimeType: string }) => {
        if (activeUrlRef.current) {
            URL.revokeObjectURL(activeUrlRef.current);
            activeUrlRef.current = null;
        }
        if ('buffer' in input) {
            const mimeType = getMimeTypeForFileName(input.fileName);
            const blob = new Blob([input.buffer], { type: mimeType });
            const url = URL.createObjectURL(blob);
            activeUrlRef.current = url;
            setState({ open: true, url, fileName: input.fileName, mimeType });
        } else {
            setState({ open: true, url: input.url, fileName: input.fileName, mimeType: input.mimeType });
        }
    }, []);

    return useMemo(() => ({ ...state, show, close }), [state, show, close]);
};

interface FilePreviewDialogProps {
    open: boolean;
    url: string | null;
    fileName: string;
    mimeType: string;
    onClose: () => void;
}

const FilePreviewDialog: React.FC<FilePreviewDialogProps> = ({ open, url, fileName, mimeType, onClose }) => {
    const isImage = mimeType.startsWith('image/');
    const isVideo = mimeType.startsWith('video/');
    const isPdf = mimeType === 'application/pdf';

    return (
        <KenModal
            open={open}
            onClose={onClose}
            title={fileName || 'Preview'}
            width="min(1000px, 92vw)"
            contentSx={{ height: '80vh', padding: 0 }}
            actions={
                url && (
                    <KenButton
                        component="a"
                        href={url}
                        download={fileName}
                        startIcon={<DownloadIcon fontSize="small" />}
                        sx={kenModalPrimaryButtonSx}
                    >
                        Download
                    </KenButton>
                )
            }
        >
            {!url ? null : isImage ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#F5F7FB', borderRadius: '12px', overflow: 'hidden' }}>
                    <img src={url} alt={fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </Box>
            ) : isVideo ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#000', borderRadius: '12px', overflow: 'hidden' }}>
                    <video src={url} controls style={{ maxWidth: '100%', maxHeight: '100%' }} />
                </Box>
            ) : isPdf ? (
                <Box component="iframe" src={url} title={fileName} sx={{ width: '100%', height: '100%', border: 'none', borderRadius: '12px' }} />
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px' }}>
                    <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                        This file type can't be previewed here — use Download below.
                    </Typography>
                </Box>
            )}
        </KenModal>
    );
};

export default FilePreviewDialog;
